/**
 * Single source of truth for the XP -> level rule used by EVERY client
 * (web dashboard, mobile API, iOS/Android apps) and by the server when it
 * stores `user_levels`.
 *
 * Rule: a student starts at level 1. Moving from level L to L+1 costs L x 300 XP
 * (300, 600, 900, ...). `levelXP` is progress inside the current level and
 * `nextLevelXP` is the size of the current level.
 *
 * Cumulative XP needed to *reach* level L is 150 x L x (L - 1).
 */
export const XP_PER_LEVEL_STEP = 300;

export interface LevelInfo {
  currentLevel: number;
  totalXP: number;
  levelXP: number;
  nextLevelXP: number;
  xpToNextLevel: number;
  /** 0..1 progress through the current level */
  progress: number;
}

export function xpForLevel(level: number): number {
  return Math.max(1, level) * XP_PER_LEVEL_STEP;
}

export function computeLevel(totalXP: number): LevelInfo {
  const total = Math.max(0, Math.floor(Number(totalXP) || 0));
  let currentLevel = 1;
  let levelXP = total;
  let nextLevelXP = xpForLevel(1);
  while (levelXP >= nextLevelXP) {
    levelXP -= nextLevelXP;
    currentLevel++;
    nextLevelXP = xpForLevel(currentLevel);
  }
  return {
    currentLevel,
    totalXP: total,
    levelXP,
    nextLevelXP,
    xpToNextLevel: nextLevelXP - levelXP,
    progress: nextLevelXP > 0 ? Math.min(1, Math.max(0, levelXP / nextLevelXP)) : 0,
  };
}

/** Build a LevelInfo from stored user_levels fields (preferred when available). */
export function levelInfoFromStored(row: { currentLevel: number; totalXP: number; levelXP: number; nextLevelXP: number }): LevelInfo {
  const nextLevelXP = row.nextLevelXP > 0 ? row.nextLevelXP : xpForLevel(row.currentLevel);
  const levelXP = Math.max(0, Math.min(row.levelXP, nextLevelXP));
  return {
    currentLevel: Math.max(1, row.currentLevel),
    totalXP: Math.max(0, row.totalXP),
    levelXP,
    nextLevelXP,
    xpToNextLevel: nextLevelXP - levelXP,
    progress: nextLevelXP > 0 ? levelXP / nextLevelXP : 0,
  };
}
